import { readVideoDuration } from "../utils/readVideoDuration.mjs";
import { apiFetch } from "../utils/apiFetch";
import React, { useEffect, useRef, useState } from "react";
import { FiLoader, FiX, FiVideo, FiImage, FiFileText, FiUpload } from "react-icons/fi";
import { Link } from "react-router";
import { useAuth } from "../context/AuthContext";
import { ownerPlan } from "../utils/communityEntitlements.mjs";
import PremiumSetupPopup from "./PremiumSetupPopup";

const NewClassPopup = ({ setNewClassPopup, workspace, setClasses, modules = [], initialModuleId = "", setupSteps, requireModule = false, onCreated }) => {
  const { userId, userInfo } = useAuth();
  const isOwner = String(workspace.creatorId?._id || workspace.creatorId) === String(userId);
  // Admins act on the owner's subscription; the server authorizes their requests.
  const canHost = workspace.store?.enabled ?? (!isOwner || ownerPlan(userInfo?.subscriptionInfo) !== "default");
  const [access, setAccess] = useState("");
  const [premiumSetup, setPremiumSetup] = useState(false);
  const [premiumReady, setPremiumReady] = useState(false);
  const chooseAccess = value => {
    if (value === "premium" && workspace.pricing?.access !== "paid" && !premiumReady) { setPremiumSetup(true); return; }
    setAccess(value);
  };
  const [saving, setSaving] = useState(false), [error, setError] = useState("");
  const savePending = useRef(false), dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current, focused = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); focused?.focus(); };
  }, []);
  const [moduleId, setModuleId] = useState(initialModuleId === "general" ? "" : initialModuleId);
  const [classTitle, setClassTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [contentType, setContentType] = useState(canHost ? "video" : "photo");
  const classType = contentType === "video" ? "prerecorded" : "resource";
  const uploadRequestRef = useRef(null);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileType = getFileType(file);
    if (fileType === "video" && !canHost) {
      setError("Choose Plus or Pro to host videos.");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }
    const actualType = file.type.startsWith("video/") ? "video" : file.type.startsWith("image/") ? "photo" : "document";
    if (actualType !== contentType) { setError("Choose a file matching the selected content type."); e.target.value = ""; return; }
    if (contentType !== "video") setClassTitle(file.name);
    setError("");
    setSelectedFile(file);
  };

  const getFileType = (file) => {
    if (file.type.startsWith("video/")) return "video";
    return "file";
  };

  const postClassToDatabase = async (payload) => {
    if (savePending.current) return;
    if (!access) { setError("Choose Free or Premium access."); return; }
    if (!payload.title?.trim() || (contentType === "video" && !payload.description?.trim()) || !classType || (requireModule && !moduleId)) {
      setError("Complete the item title, description and type.");
      return;
    }
    savePending.current = true; setSaving(true); setError("");
    try {
      const postToDBRes = await apiFetch(`${process.env.API_URL}/api/newclass`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body: JSON.stringify({ ...payload, access, moduleId: moduleId || null }),
      });

      const DBjson = await postToDBRes.json();

      if (!postToDBRes.ok) {
        throw new Error(postToDBRes.status + ": " + DBjson.message);
      }

      setClasses((prev) => [...prev, DBjson.cls]);
      if (onCreated) onCreated(DBjson.cls);
      else setNewClassPopup(null);
    } catch (err) {
      setError(err.message);
    } finally {
      savePending.current = false; setSaving(false); setIsUploading(false);
    }
  };

  const uploadClass = async () => {
    if (isUploading || saving) return;
    if (!access) { setError("Choose Free or Premium access."); return; }
    if (!selectedFile || (contentType === "video" && (!classTitle.trim() || !description.trim())) || !classType) {
      setError(contentType === "video" ? "Add a video, title and description." : "Choose a file to upload.");
      return;
    }

    const fileType = getFileType(selectedFile);

    setLoadingSpinner(true);
    setUploadProgress(0);
    setIsUploading(true);

    try {
      const duration = fileType === "video" ? await readVideoDuration(selectedFile) : null;
      let thumbnailToken;
      if (contentType === "video" && thumbnailFile) {
        const response = await apiFetch(`${process.env.API_URL}/api/groups/thumbnail`, { method: "POST", headers: { "Content-Type": thumbnailFile.type }, body: thumbnailFile });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not upload thumbnail");
        thumbnailToken = data.thumbnailToken;
      }
      const sigRes = await apiFetch(
        `${process.env.API_URL}/api/r2/get-signed-upload-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",

          },
          body: JSON.stringify({
            workspaceId: workspace._id,
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            fileType: selectedFile.type,
            folder: fileType === "video" ? "classes/videos" : "classes/files",
          }),
        },
      );

      const signature = await sigRes.json();
      if (!sigRes.ok) {
        throw new Error(signature.message || "Failed to get upload URL");
      }

      const { uploadUrl, key, publicUrl, uploadToken } = signature;
      if (!uploadToken) throw new Error("Upload authorization is missing. Please retry.");

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        uploadRequestRef.current = xhr;

        xhr.open("PUT", uploadUrl);
        xhr.setRequestHeader(
          "Content-Type",
          selectedFile.type || "application/octet-stream",
        );

        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;

          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        };

        xhr.onload = () => {
          uploadRequestRef.current = null;
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        };

        xhr.onerror = () => {
          uploadRequestRef.current = null;
          reject(new Error("Network error during upload"));
        };

        xhr.onabort = () => {
          uploadRequestRef.current = null;
          reject(new Error("Upload cancelled"));
        };

        xhr.send(selectedFile);
      });

      const payload = {
        thumbnailToken,
        uploadToken,
        workspaceId: workspace._id,
        title: contentType === "video" ? classTitle.trim() : selectedFile.name,
        description: contentType === "document" ? "" : description.trim(),
        type: classType,
        video:
          classType === "prerecorded"
            ? {
                duration,
                url: publicUrl,
                key,
                name: selectedFile.name,
                mime: selectedFile.type,
                size: selectedFile.size,
              }
            : {},
        attachments:
          classType === "resource"
            ? [
                {
                  url: publicUrl,
                  key,
                  name: selectedFile.name,
                  mime: selectedFile.type,
                  size: selectedFile.size,
                },
              ]
            : [],
        status: null,
      };

      await postClassToDatabase(payload);
      setLoadingSpinner(false);
      setUploadProgress(null);

      // const postToDBRes = await apiFetch(`${process.env.API_URL}/api/newclass`, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //
      //   },
      //   body: JSON.stringify(payload),
      // });

      // if (!postToDBRes.ok) {
      //   throw new Error(postToDBRes.status + ": " + postToDBRes.statusText);
      // }

      // const DBjson = await postToDBRes.json();
      // setClasses((prev) => [...prev, DBjson.cls]);

      // setLoadingSpinner(false);
      // setUploadProgress(null);
      // setIsUploading(false);
      // setNewClassPopup(null);
    } catch (err) {
      setLoadingSpinner(false);
      setUploadProgress(null);
      setIsUploading(false);
      setError(err.message);
    }
  };

  const cancelUpload = () => {
    uploadRequestRef.current?.abort();
  };

  const busy = isUploading || saving;
  const fieldClass = "mt-2 block w-full rounded-lg border border-[var(--app-border)] bg-[var(--app-muted)] px-3 py-2.5 text-[var(--app-text)] outline-none focus:ring-2 focus:ring-gray-400";
  return <dialog ref={dialogRef} aria-label="Add Content" onCancel={event => { event.preventDefault(); if (!busy) setNewClassPopup(false); }} className="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[85dvh] overflow-y-auto rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-5 text-[var(--app-text)] shadow-2xl backdrop:bg-black/60 sm:p-6">
    <div className="mb-6 flex items-center justify-between gap-2">
      <h2 className="text-lg font-semibold">Add Content</h2>
      <div className="flex items-center gap-2">
        <select aria-label="Resource access" value={access} disabled={busy} onChange={event => chooseAccess(event.target.value)} className="min-h-9 rounded-lg border border-[var(--app-border)] bg-[var(--app-muted)] px-2 text-sm"><option value="" disabled>Free / Premium</option><option value="free">Free</option><option value="premium">Premium</option></select>
        <button type="button" aria-label="Close" disabled={busy} onClick={() => setNewClassPopup(false)} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-[var(--app-muted)] disabled:opacity-50"><FiX className="h-5 w-5" /></button>
      </div>
    </div>
    <form onSubmit={event => { event.preventDefault(); uploadClass(); }} className="space-y-5">
      <fieldset disabled={busy}><legend className="mb-2 text-sm font-semibold">What would you like to add?</legend><div className="grid grid-cols-3 gap-2">
        {[["video", "Video", FiVideo], ["photo", "Photo", FiImage], ["document", "Document", FiFileText]].map(([value, label, Icon]) => <button key={value} type="button" aria-pressed={contentType === value} onClick={() => { setContentType(value); setSelectedFile(null); setThumbnailFile(null); setClassTitle(""); setDescription(""); setError(""); }} className={`flex flex-col items-center gap-2 rounded-xl border p-3 text-sm font-semibold ${contentType === value ? "border-[var(--app-text)] bg-[var(--app-muted)]" : "border-[var(--app-border)] hover:bg-[var(--app-muted)]"}`}><Icon className="h-5 w-5" />{label}</button>)}
      </div></fieldset>
      {contentType === "video" && !canHost && <Link to="/choose-plan" className="block text-sm underline">Upgrade to Plus or Pro to host videos</Link>}
      {contentType === "video" && <label className="block text-sm font-medium">Title<input required disabled={busy} maxLength={300} value={classTitle} onChange={event => setClassTitle(event.target.value)} className={fieldClass} /></label>}
      {contentType !== "document" && <label className="block text-sm font-medium">{contentType === "video" ? "Description" : "Caption"}<textarea required={contentType === "video"} disabled={busy} rows={3} value={description} onChange={event => setDescription(event.target.value)} className={fieldClass} /></label>}
      <label className="block rounded-xl border border-dashed border-[var(--app-border)] bg-[var(--app-muted)] p-4 text-sm"><span className="mb-3 flex items-center gap-2 font-medium"><FiUpload />Choose {contentType === "photo" ? "a photo" : contentType === "video" ? "a video" : "a document"}</span><input key={contentType} type="file" required disabled={busy || contentType === "video" && !canHost} accept={contentType === "video" ? "video/mp4,video/webm,video/quicktime" : contentType === "photo" ? "image/jpeg,image/png,image/webp,image/gif" : ".pdf,.doc,.docx,.ppt,.pptx,.txt"} onChange={onSelectFile} className="block w-full min-w-0 text-sm file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-2" /></label>
      {contentType === "video" && <label className="block text-sm font-medium">Thumbnail <span className="font-normal text-gray-500">(optional)</span><input aria-label="Video thumbnail (optional)" type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={event => {
        const file = event.target.files?.[0];
        if (file && (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024)) { setError("Choose a JPG, PNG, or WebP thumbnail up to 5 MB."); event.target.value = ""; setThumbnailFile(null); return; }
        setThumbnailFile(file || null); setError("");
      }} className="mt-2 block w-full rounded-lg border border-[var(--app-border)] bg-[var(--app-muted)] p-3 text-sm" /><span className="mt-1 block text-xs font-normal text-gray-500">JPG, PNG or WebP, up to 5 MB. This image is used as the video's preview.</span></label>}
      {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
      {isUploading && <div role="status" className="text-sm">{saving ? "Saving content..." : `Uploading ${uploadProgress || 0}%`}<progress value={uploadProgress || 0} max="100" className="mt-2 block h-1.5 w-full" /></div>}
      <div className="flex justify-end gap-3">{isUploading && !saving && <button type="button" onClick={cancelUpload} className="rounded-lg px-4 py-2 text-sm">Cancel upload</button>}<button disabled={busy || contentType === "video" && !canHost} aria-busy={busy} className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-orange-700 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy && <FiLoader className="animate-spin" />}Add Content</button></div>
    </form>
    {premiumSetup && <PremiumSetupPopup onClose={() => setPremiumSetup(false)} onComplete={() => { setPremiumReady(true); setAccess("premium"); setPremiumSetup(false); }} />}
  </dialog>;
};
export default NewClassPopup;
